// ═══════════════════════════════════════════════════════════════
// VISTA: Detalles de Nómina Cerrada
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Download,
  FileText,
  Eye,
  Lock,
  AlertCircle,
  TrendingUp,
  Users,
  DollarSign,
  Calendar,
  ChevronDown,
  ChevronUp,
  Calculator,
  X,
  RefreshCw,
  CheckCircle
} from 'lucide-react';
import { usePayroll } from '../../../hooks/usePayroll';
import { recalculatePayroll } from '../../../api/payroll';

import Pagination from '../../common/Pagination';

export default function PayrollDetailsView({ payroll, onBack }) {
  const { getPayrollDetails, getPayrollSummary, getAccountingSummary, exportToExcel, exportToPDF, exportAccountingToExcel, exportAccountingToPDF, downloadPayslip, downloadBulkPayslips, loading } = usePayroll();

  const [details, setDetails] = useState(null);
  const [summary, setSummary] = useState(null);
  const [expandedEmployees, setExpandedEmployees] = useState({});
  const [showAccountingModal, setShowAccountingModal] = useState(false);
  const [accountingData, setAccountingData] = useState(null);
  const [showReprocessModal, setShowReprocessModal] = useState(false);
  const [reprocessReason, setReprocessReason] = useState('');
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessResult, setReprocessResult] = useState(null);

  // Pagination for employee records table
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 12;

  // Helper function to safely parse JSON fields
  const safeJSONParse = (field) => {
    if (!field) return [];

    if (Array.isArray(field)) return field;

    if (typeof field === 'object') {
      return Object.entries(field).map(([tipo, monto]) => ({ tipo, monto }));
    }

    try {
      const parsed = JSON.parse(field);
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === 'object') {
        return Object.entries(parsed).map(([tipo, monto]) => ({ tipo, monto }));
      }
      return [];
    } catch (e) {
      console.error('Error parsing JSON:', e);
      return [];
    }
  };

  useEffect(() => {
    loadDetails();
    loadSummary();
  }, [payroll.id]);

  const loadDetails = async () => {
    try {
      const data = await getPayrollDetails(payroll.id);
      setDetails(data.records || data);
    } catch (error) {
      console.error('Error loading details:', error);
    }
  };

  const loadSummary = async () => {
    try {
      const data = await getPayrollSummary(payroll.id);
      setSummary(data.summary || data);
    } catch (error) {
      console.error('Error loading summary:', error);
    }
  };

  const toggleEmployeeDetails = (empId) => {
    setExpandedEmployees(prev => ({
      ...prev,
      [empId]: !prev[empId]
    }));
  };

  const handleExport = async () => {
    try {
      await exportToExcel(payroll.id);
    } catch (error) {
      alert('Error al exportar: ' + error.message);
    }
  };

  const handleExportPDF = async () => {
    try {
      await exportToPDF(payroll.id);
    } catch (error) {
      alert('Error al exportar PDF: ' + error.message);
    }
  };

  const handleDownloadPayslips = async () => {
    try {
      await downloadBulkPayslips(payroll.id);
    } catch (error) {
      alert('Error al descargar comprobantes: ' + error.message);
    }
  };

  const handleShowAccounting = async () => {
    try {
      const data = await getAccountingSummary(payroll.id);
      setAccountingData(data.resumen);
      setShowAccountingModal(true);
    } catch (error) {
      alert('Error al cargar resumen contable: ' + error.message);
    }
  };

  const handleReprocess = async () => {
    if (!reprocessReason.trim()) return;
    try {
      setReprocessing(true);
      setReprocessResult(null);
      const result = await recalculatePayroll(payroll.id, reprocessReason);
      setReprocessResult(result);
      await loadDetails();
      await loadSummary();
    } catch (error) {
      setReprocessResult({ success: false, error: error.message });
    } finally {
      setReprocessing(false);
    }
  };

  const formatCurrency = (value) => {
    return parseFloat(value || 0).toLocaleString('es-DO', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const getEstadoBadge = (estado) => {
    const badges = {
      closed: { color: 'green', label: 'Cerrada', icon: Lock },
      cancelled: { color: 'red', label: 'Anulada', icon: AlertCircle }
    };

    const badge = badges[estado] || badges.closed;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-${badge.color}-100 text-${badge.color}-700`}>
        <Icon size={12} />
        {badge.label}
      </span>
    );
  };

  // ── Loading state (inline, not full screen) ───────────────────
  if (loading || !details || !summary) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Cargando detalles...</p>
        </div>
      </div>
    );
  }

  // Pagination calculations
  const detailsArray = details && Array.isArray(details) ? details : [];
  const totalPages   = Math.ceil(detailsArray.length / PER_PAGE);
  const start        = (currentPage - 1) * PER_PAGE;
  const currentRecords = detailsArray.slice(start, start + PER_PAGE);

  return (
    <div className="space-y-4">

      {/* ── Header ─────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
        >
          <ArrowLeft size={16} />
          Volver
        </button>

        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => { setShowReprocessModal(true); setReprocessResult(null); setReprocessReason(''); }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-orange-200 text-orange-600 rounded-lg hover:bg-orange-50 transition-colors"
            disabled={loading}
            title="Recalcular todos los empleados de esta nómina"
          >
            <RefreshCw size={14} />
            Reprocesar
          </button>

          <button
            onClick={handleShowAccounting}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            <Calculator size={14} />
            Resumen Contable
          </button>

          <button
            onClick={handleDownloadPayslips}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            <FileText size={14} />
            Comprobantes (ZIP)
          </button>

          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={loading}
          >
            <Download size={14} />
            Excel
          </button>

          <button
            onClick={handleExportPDF}
            className="flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold"
            disabled={loading}
          >
            <FileText size={14} />
            PDF
          </button>
        </div>
      </div>

      {/* ── Título + meta ──────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Detalles de Nómina</h1>
          <p className="text-sm text-gray-400 mt-0.5">
            {payroll.periodo} • {payroll.quincena} • {payroll.tipo}
          </p>
        </div>
        <div className="ml-1">{getEstadoBadge(payroll.estado)}</div>
      </div>

      {/* ── Info General ───────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
          <Calendar size={14} className="text-gray-400" />
          Información General
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <p className="text-xs text-gray-400 mb-1">Fecha Inicio</p>
            <p className="text-sm font-semibold text-gray-900">
              {payroll.fecha_inicio ? new Date(payroll.fecha_inicio.split('T')[0] + 'T12:00:00').toLocaleDateString('es-DO') : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Fecha Fin</p>
            <p className="text-sm font-semibold text-gray-900">
              {payroll.fecha_fin ? new Date(payroll.fecha_fin.split('T')[0] + 'T12:00:00').toLocaleDateString('es-DO') : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Fecha Pago</p>
            <p className="text-sm font-semibold text-gray-900">
              {payroll.fecha_pago ? new Date(payroll.fecha_pago.split('T')[0] + 'T12:00:00').toLocaleDateString('es-DO') : '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-gray-400 mb-1">Procesada Por</p>
            <p className="text-sm font-semibold text-gray-900">
              {payroll.created_by_name || 'Sistema'}
            </p>
          </div>
        </div>
        {payroll.descripcion && (
          <div className="mt-4 p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700">{payroll.descripcion}</p>
          </div>
        )}
      </div>

      {/* ── Summary Cards ──────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <Users size={18} className="text-blue-500" />
            <span className="text-2xl font-bold text-blue-600">{summary.total_empleados}</span>
          </div>
          <p className="text-xs text-gray-400 font-medium">Empleados</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <TrendingUp size={18} className="text-green-500" />
            <p className="text-sm font-bold text-green-600 text-right">
              RD$ {formatCurrency(summary.total_bruto)}
            </p>
          </div>
          <p className="text-xs text-gray-400 font-medium">Total Bruto</p>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <FileText size={18} className="text-red-500" />
            <p className="text-sm font-bold text-red-600 text-right">
              RD$ {formatCurrency(summary.total_deducciones)}
            </p>
          </div>
          <p className="text-xs text-gray-400 font-medium">Deducciones</p>
        </div>

        <div className="bg-white border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <DollarSign size={18} className="text-blue-500" />
            <p className="text-sm font-bold text-blue-600 text-right">
              RD$ {formatCurrency(summary.total_neto)}
            </p>
          </div>
          <p className="text-xs text-blue-500 font-medium">Total Neto</p>
        </div>
      </div>

      {/* ── Desglose Contable ──────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">Desglose Contable</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-xs text-green-600 mb-1">Salarios Base</p>
            <p className="text-base font-bold text-green-700">
              RD$ {formatCurrency(summary.total_salarios_base)}
            </p>
          </div>
          <div className="bg-green-50 rounded-lg p-4">
            <p className="text-xs text-green-600 mb-1">Ingresos Variables</p>
            <p className="text-base font-bold text-green-700">
              RD$ {formatCurrency(summary.total_ingresos_variables)}
            </p>
          </div>
          <div className="bg-blue-50 rounded-lg p-4">
            <p className="text-xs text-blue-600 mb-1">TSS</p>
            <p className="text-base font-bold text-blue-700">
              RD$ {formatCurrency(summary.total_tss)}
            </p>
          </div>
          <div className="bg-orange-50 rounded-lg p-4">
            <p className="text-xs text-orange-600 mb-1">ISR</p>
            <p className="text-base font-bold text-orange-700">
              RD$ {formatCurrency(summary.total_isr)}
            </p>
          </div>
          <div className="bg-red-50 rounded-lg p-4">
            <p className="text-xs text-red-600 mb-1">Préstamos</p>
            <p className="text-base font-bold text-red-700">
              RD$ {formatCurrency(summary.total_prestamos)}
            </p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4">
            <p className="text-xs text-purple-600 mb-1">Costo Patronal</p>
            <p className="text-base font-bold text-purple-700">
              RD$ {formatCurrency(summary.total_costo_patronal)}
            </p>
          </div>
        </div>
      </div>

      {/* ── Registros por Empleado (tabla con paginación) ─────── */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
          <Eye size={14} className="text-gray-400" />
          <p className="text-sm font-semibold text-gray-700">
            Registros por Empleado
            <span className="ml-2 text-xs font-normal text-gray-400">{detailsArray.length} empleados</span>
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[780px]">
            <thead>
              <tr className="border-b border-gray-200">
                {['EMPLEADO', 'BRUTO', 'DEDUCCIONES', 'NETO', ''].map((h) => (
                  <th
                    key={h}
                    className={`px-4 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider ${h === '' ? 'text-center w-12' : 'text-left'}`}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {currentRecords.map((record) => {
                const isExpanded = expandedEmployees[record.employee_id];
                return (
                  <React.Fragment key={record.id}>
                    <tr
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => toggleEmployeeDetails(record.employee_id)}
                    >
                      <td className="px-4 py-3">
                        <p className="text-sm font-semibold text-gray-900">{record.employee_name}</p>
                        <p className="text-xs text-gray-400 font-mono">{record.employee_code}</p>
                      </td>
                      <td className="px-4 py-3 tabular-nums text-sm font-semibold text-gray-900">
                        RD$ {formatCurrency(record.total_bruto)}
                      </td>
                      <td className="px-4 py-3 tabular-nums text-sm text-red-600 font-semibold">
                        -RD$ {formatCurrency(record.total_deducciones)}
                      </td>
                      <td className="px-4 py-3 tabular-nums text-sm font-bold text-blue-700">
                        RD$ {formatCurrency(record.neto_a_pagar)}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {isExpanded
                          ? <ChevronUp size={14} className="text-gray-400 mx-auto" />
                          : <ChevronDown size={14} className="text-gray-400 mx-auto" />
                        }
                      </td>
                    </tr>

                    {/* Fila expandible */}
                    {isExpanded && (
                      <tr>
                        <td colSpan="5" className="px-4 py-4 bg-gray-50">
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            <div className="bg-white rounded-lg p-4 border border-gray-100">
                              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Ingresos</p>
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Salario Base:</span>
                                  <span className="font-semibold text-gray-800">{formatCurrency(record.salario_base)}</span>
                                </div>
                                {record.ingresos_variables && safeJSONParse(record.ingresos_variables).map((ing, idx) => (
                                  <div key={idx} className="flex justify-between">
                                    <span className="capitalize text-gray-500">{ing.tipo.replace(/_/g, ' ')}:</span>
                                    <span className="font-semibold text-gray-800">{formatCurrency(ing.monto)}</span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="bg-white rounded-lg p-4 border border-gray-100">
                              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">Deducciones</p>
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between">
                                  <span className="text-gray-500">TSS:</span>
                                  <span className="font-semibold text-gray-800">{formatCurrency(record.tss_empleado)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">ISR:</span>
                                  <span className="font-semibold text-gray-800">{formatCurrency(record.isr_monto)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Préstamos:</span>
                                  <span className="font-semibold text-gray-800">{formatCurrency(record.total_prestamos)}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-500">Otras Ded.:</span>
                                  <span className="font-semibold text-gray-800">{formatCurrency(record.total_otras_deducciones)}</span>
                                </div>
                              </div>
                            </div>

                            <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
                              <p className="text-[10px] font-semibold text-blue-400 uppercase tracking-wider mb-2">Resumen</p>
                              <div className="space-y-1 text-xs">
                                <div className="flex justify-between pt-1 border-t border-blue-200">
                                  <span className="font-bold text-blue-700">NETO:</span>
                                  <span className="font-bold text-blue-700 text-sm">
                                    {formatCurrency(record.neto_a_pagar)}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <Pagination
          page={currentPage}
          totalPages={totalPages}
          total={detailsArray.length}
          pageSize={PER_PAGE}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* ── Modal: Resumen Contable ─────────────────────────────── */}
      {showAccountingModal && accountingData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Header del Modal */}
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-5 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Calculator size={20} />
                <div>
                  <h2 className="text-lg font-bold">Resumen Contable</h2>
                  <p className="text-purple-100 text-xs">
                    {accountingData.nomina.periodo} - {accountingData.nomina.quincena || 'Mensual'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowAccountingModal(false)}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-all"
              >
                <X size={18} />
              </button>
            </div>

            {/* Contenido del Modal */}
            <div className="p-5 space-y-5">
              {/* Información de la Nómina */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <Calendar size={14} className="text-purple-500" />
                  Información de la Nómina
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <p className="text-xs text-gray-400">Tipo</p>
                    <p className="font-semibold text-gray-900">{accountingData.nomina.tipo.toUpperCase()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Estado</p>
                    <p className="font-semibold text-gray-900">{accountingData.nomina.estado.toUpperCase()}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Fecha Pago</p>
                    <p className="font-semibold text-gray-900">
                      {accountingData.nomina.fecha_pago ? new Date(accountingData.nomina.fecha_pago.split('T')[0] + 'T12:00:00').toLocaleDateString('es-DO') : '—'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Total Empleados</p>
                    <p className="font-semibold text-gray-900">{accountingData.nomina.total_empleados}</p>
                  </div>
                </div>
              </div>

              {/* Grid de Totales Principales */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                  <h4 className="text-xs text-green-600 font-semibold mb-1">Total Ingresos</h4>
                  <p className="text-xl font-bold text-green-700">
                    RD$ {formatCurrency(accountingData.totales.total_ingresos)}
                  </p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <h4 className="text-xs text-red-600 font-semibold mb-1">Total Deducciones</h4>
                  <p className="text-xl font-bold text-red-700">
                    RD$ {formatCurrency(accountingData.totales.total_deducciones)}
                  </p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <h4 className="text-xs text-purple-600 font-semibold mb-1">Neto a Pagar</h4>
                  <p className="text-xl font-bold text-purple-700">
                    RD$ {formatCurrency(accountingData.totales.neto_a_pagar)}
                  </p>
                </div>
              </div>

              {/* Desglose de Ingresos */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <TrendingUp size={14} className="text-green-500" />
                  Desglose de Ingresos
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">Salarios</p>
                    <p className="text-sm font-bold text-green-700">RD$ {formatCurrency(accountingData.ingresos.salarios)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Horas Extras</p>
                    <p className="text-sm font-bold text-green-700">RD$ {formatCurrency(accountingData.ingresos.horas_extras)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Comisiones</p>
                    <p className="text-sm font-bold text-green-700">RD$ {formatCurrency(accountingData.ingresos.comisiones)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Incentivos</p>
                    <p className="text-sm font-bold text-green-700">RD$ {formatCurrency(accountingData.ingresos.incentivos)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Bonificaciones</p>
                    <p className="text-sm font-bold text-green-700">RD$ {formatCurrency(accountingData.ingresos.bonificaciones)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Vacaciones</p>
                    <p className="text-sm font-bold text-green-700">RD$ {formatCurrency(accountingData.ingresos.vacaciones)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Otros Ingresos</p>
                    <p className="text-sm font-bold text-green-700">RD$ {formatCurrency(accountingData.ingresos.otros_ingresos)}</p>
                  </div>
                </div>
              </div>

              {/* Desglose de Deducciones */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <DollarSign size={14} className="text-red-500" />
                  Desglose de Deducciones
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">AFP Empleado</p>
                    <p className="text-sm font-bold text-red-700">RD$ {formatCurrency(accountingData.deducciones.afp_empleado)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">SFS Empleado</p>
                    <p className="text-sm font-bold text-red-700">RD$ {formatCurrency(accountingData.deducciones.sfs_empleado)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">ISR</p>
                    <p className="text-sm font-bold text-red-700">RD$ {formatCurrency(accountingData.deducciones.isr)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Préstamos</p>
                    <p className="text-sm font-bold text-red-700">RD$ {formatCurrency(accountingData.deducciones.prestamos)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">Otras</p>
                    <p className="text-sm font-bold text-red-700">RD$ {formatCurrency(accountingData.deducciones.otras)}</p>
                  </div>
                </div>
              </div>

              {/* Aportes Patronales */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <Users size={14} className="text-blue-500" />
                  Aportes Patronales
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="text-xs text-gray-400">AFP Patronal</p>
                    <p className="text-sm font-bold text-blue-700">RD$ {formatCurrency(accountingData.aportes_patronales.afp_patronal)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">SFS Patronal</p>
                    <p className="text-sm font-bold text-blue-700">RD$ {formatCurrency(accountingData.aportes_patronales.sfs_patronal)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">SRL</p>
                    <p className="text-sm font-bold text-blue-700">RD$ {formatCurrency(accountingData.aportes_patronales.srl)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">INFOTEP</p>
                    <p className="text-sm font-bold text-blue-700">RD$ {formatCurrency(accountingData.aportes_patronales.infotep)}</p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-blue-200 flex justify-between items-center">
                  <p className="text-sm font-semibold text-gray-700">Total Aportes Patronales:</p>
                  <p className="text-base font-bold text-blue-700">
                    RD$ {formatCurrency(accountingData.totales.total_aportes_patronales)}
                  </p>
                </div>
              </div>

              {/* Desglose TSS */}
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <h3 className="text-sm font-semibold text-gray-700 mb-4">Resumen para TSS</h3>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-semibold text-gray-700">AFP Total (Empleado + Patronal):</span>
                    <span className="text-sm font-bold text-blue-700">
                      RD$ {formatCurrency(accountingData.desglose_tss.afp.total)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-semibold text-gray-700">SFS Total (Empleado + Patronal):</span>
                    <span className="text-sm font-bold text-blue-700">
                      RD$ {formatCurrency(accountingData.desglose_tss.sfs.total)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-semibold text-gray-700">SRL:</span>
                    <span className="text-sm font-bold text-blue-700">
                      RD$ {formatCurrency(accountingData.desglose_tss.srl.total)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-semibold text-gray-700">INFOTEP:</span>
                    <span className="text-sm font-bold text-blue-700">
                      RD$ {formatCurrency(accountingData.desglose_tss.infotep.total)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <span className="text-sm font-bold text-gray-800">Total TSS a Pagar:</span>
                    <span className="text-base font-bold text-purple-700">
                      RD$ {formatCurrency(accountingData.desglose_tss.total_tss)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Costo Total para la Empresa */}
              <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-xl p-5 text-white">
                <h3 className="font-bold text-base mb-1">Costo Total para la Empresa</h3>
                <p className="text-purple-100 text-xs mb-3">Ingresos + Aportes Patronales</p>
                <p className="text-3xl font-bold">
                  RD$ {formatCurrency(accountingData.totales.costo_total_empresa)}
                </p>
              </div>
            </div>

            {/* Footer del Modal */}
            <div className="sticky bottom-0 bg-gray-50 px-5 py-4 rounded-b-2xl border-t flex justify-between items-center">
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    try {
                      await exportAccountingToExcel(payroll.id);
                    } catch (error) {
                      alert('Error al exportar: ' + error.message);
                    }
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-all"
                  disabled={loading}
                >
                  <Download size={14} />
                  Exportar Excel
                </button>
                <button
                  onClick={async () => {
                    try {
                      await exportAccountingToPDF(payroll.id);
                    } catch (error) {
                      alert('Error al exportar: ' + error.message);
                    }
                  }}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-all"
                  disabled={loading}
                >
                  <FileText size={14} />
                  Exportar PDF
                </button>
              </div>
              <button
                onClick={() => setShowAccountingModal(false)}
                className="px-4 py-2 text-sm border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-all"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Reprocesar Nómina ──────────────────────────── */}
      {showReprocessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
            {/* Header */}
            <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white p-5 rounded-t-2xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <RefreshCw size={20} />
                <div>
                  <h2 className="text-base font-bold">Reprocesar Nómina</h2>
                  <p className="text-orange-100 text-xs">{payroll.periodo} · {payroll.quincena || 'Mensual'}</p>
                </div>
              </div>
              <button onClick={() => setShowReprocessModal(false)} className="p-1.5 hover:bg-white/20 rounded-lg transition-all">
                <X size={16} />
              </button>
            </div>

            <div className="p-5 space-y-4">
              {/* Resultado */}
              {reprocessResult && (
                <div className={`p-4 rounded-xl border ${reprocessResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  {reprocessResult.success ? (
                    <div className="flex items-start gap-2">
                      <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-green-800">Reprocesamiento completado</p>
                        <p className="text-xs text-green-700 mt-1">
                          {reprocessResult.exitosos} de {reprocessResult.total_empleados} empleados recalculados exitosamente.
                        </p>
                        {reprocessResult.errores?.length > 0 && (
                          <p className="text-xs text-orange-700 mt-1">
                            {reprocessResult.errores.length} empleado(s) con error. Revisa los logs de la nómina.
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-2">
                      <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-red-700">{reprocessResult.error}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Advertencia */}
              {!reprocessResult && (
                <div className="flex items-start gap-2 p-4 bg-orange-50 border border-orange-200 rounded-xl">
                  <AlertCircle size={15} className="text-orange-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-orange-800">
                    <p className="font-semibold">Esta acción recalculará todos los empleados de la nómina.</p>
                    <p className="mt-1 text-xs">Se usarán los salarios y tasas vigentes en la fecha de la nómina. La acción queda registrada en el log de auditoría.</p>
                  </div>
                </div>
              )}

              {/* Motivo */}
              {!reprocessResult && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Motivo del reprocesamiento <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    rows={3}
                    value={reprocessReason}
                    onChange={e => setReprocessReason(e.target.value)}
                    placeholder="Ej: Corrección de cálculo AFP/SFS tras actualización del motor de nómina"
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400 resize-none"
                  />
                </div>
              )}

              {/* Botones */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowReprocessModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-gray-700 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors"
                >
                  {reprocessResult ? 'Cerrar' : 'Cancelar'}
                </button>
                {!reprocessResult && (
                  <button
                    onClick={handleReprocess}
                    disabled={reprocessing || !reprocessReason.trim()}
                    className="flex-1 px-4 py-2.5 text-sm font-semibold bg-orange-600 text-white rounded-xl hover:bg-orange-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                  >
                    {reprocessing ? (
                      <><RefreshCw size={14} className="animate-spin" /> Reprocesando...</>
                    ) : (
                      <><RefreshCw size={14} /> Confirmar Reprocesamiento</>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
